import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { resolveAsset } from "@/lib/api";
import { getBrandInitial } from "@/lib/brandLogos";

const BrandLogo = ({ name, logoUrl }) => {
    const [failed, setFailed] = useState(false);
    const src = logoUrl ? resolveAsset(logoUrl) : "";

    if (!src || failed) {
        return (
            <span
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-zinc-100 text-[#0A2540] flex items-center justify-center text-lg font-bold shrink-0"
                aria-hidden
            >
                {getBrandInitial(name)}
            </span>
        );
    }

    return (
        <img
            src={src}
            alt={`${name} logo`}
            className="h-10 sm:h-12 w-auto max-w-[120px] object-contain"
            loading="lazy"
            onError={() => setFailed(true)}
        />
    );
};

const HomeBrandsSection = ({ pages = {} }) => {
    const [brands, setBrands] = useState([]);

    useEffect(() => {
        api.get("/brands")
            .then((r) => setBrands(r.data?.items || []))
            .catch(() => setBrands([]));
    }, []);

    if (brands.length === 0) return null;

    return (
        <section className="py-10 sm:py-16 bg-white border-t border-zinc-100" data-testid="home-brands-section">
            <div className="container-x">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <p className="text-[11px] font-semibold text-[#E31837] tracking-wide uppercase">{pages.home_brands_eyebrow || "Authorized partners"}</p>
                        <h2 className="font-display text-3xl sm:text-4xl text-[#0A2540] mt-2 leading-tight">
                            {pages.home_brands_title || "Brands we deal with"}
                        </h2>
                        <p className="text-sm text-zinc-500 mt-2 max-w-lg">
                            {pages.home_brands_sub || "Honda, Hero, Bajaj, TVS, Royal Enfield, and more — new and pre-booked models across our outlets."}
                        </p>
                    </motion.div>
                    <Link
                        to="/bikes"
                        className="inline-flex items-center gap-1 text-sm font-semibold text-[#E31837] hover:underline shrink-0"
                        data-testid="home-brands-view-all"
                    >
                        View all bikes <ArrowRight size={14} />
                    </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                    {brands.map((brand, i) => (
                        <motion.div
                            key={brand.id || brand.name}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: Math.min(i * 0.03, 0.25) }}
                        >
                            <Link
                                to={`/bikes?brand=${encodeURIComponent(brand.name)}`}
                                className="flex flex-col items-center justify-center text-center rounded-2xl border border-zinc-200 p-4 sm:p-5 min-h-[120px] hover:shadow-md hover:-translate-y-0.5 transition-all bg-white hover:border-[#E31837]/30 group"
                                data-testid={`home-brand-${brand.name.replace(/\s/g, "-")}`}
                            >
                                <div className="h-12 sm:h-14 flex items-center justify-center mb-3 w-full px-2">
                                    <BrandLogo name={brand.name} logoUrl={brand.logo_url} />
                                </div>
                                <span className="font-heading font-semibold text-sm text-[#0A2540] group-hover:text-[#E31837] leading-tight">
                                    {brand.name}
                                </span>
                                <span className="text-[11px] text-zinc-500 mt-1">
                                    {brand.model_count} model{brand.model_count === 1 ? "" : "s"}
                                </span>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default HomeBrandsSection;
